import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import AdminLayout from "./AdminLayout";
import { useAuth } from "../../context/AuthContext";
import {
  DEFAULT_WEBSITE,
  DEFAULT_NAV,
  useWebsite,
  useServices,
} from "../../context/WebsiteContext";
const field =
  "w-full min-w-0 min-h-[44px] border rounded-lg px-3 py-2 bg-white text-sm focus:ring-2 focus:ring-[#4A90A4]";
const button =
  "min-h-[44px] px-3 py-2 border rounded-lg text-sm disabled:opacity-40";
function ImageField({ value, onChange, token, label }) {
  const [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  return (
    <div className="space-y-2">
      <label className="block text-sm">
        {label}
        <input
          className={field}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
        />
      </label>
      <label className="block text-sm">
        Upload image
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          disabled={busy}
          className="block w-full text-sm py-2"
          onChange={async (e) => {
            const f = e.target.files?.[0];
            if (!f) return;
            setBusy(true);
            setError("");
            try {
              const body = new FormData();
              body.append("file", f);
              const r = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/uploads/admin`,
                body,
                { headers: { Authorization: `Bearer ${token}` } },
              );
              onChange(r.data.public_url);
            } catch {
              setError("Upload failed. Please try again.");
            } finally {
              setBusy(false);
              e.target.value = "";
            }
          }}
        />
      </label>
      {busy && <p>Uploading…</p>}
      {error && <p role="alert">{error}</p>}
      {value && (
        <img
          src={
            value.startsWith("/api/")
              ? `${process.env.REACT_APP_BACKEND_URL}${value}`
              : value
          }
          alt="Current image"
          className="h-24 max-w-full object-contain"
        />
      )}
    </div>
  );
}
export default function AdminWebsite() {
  const { token } = useAuth(),
    { refresh } = useWebsite(),
    services = useServices();
  const [form, setForm] = useState(null),
    [error, setError] = useState(""),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false),
    [section, setSection] = useState("navigation");
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/website`)
      .then(({ data }) =>
        setForm({
          ...DEFAULT_WEBSITE,
          ...data,
          navigation: data.navigation ?? DEFAULT_NAV,
        }),
      )
      .catch(() =>
        setError("Website settings could not be loaded. Reload to try again."),
      );
  }, []);
  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setMessage("");
  };
  const updateService = (slug, key, value) => {
    setForm((f) => ({
      ...f,
      services: {
        ...f.services,
        [slug]: { ...f.services[slug], [key]: value },
      },
    }));
    setMessage("");
  };
  const save = async () => {
    setBusy(true);
    setError("");
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/website`,
        form,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await refresh();
      setMessage(
        "Saved. Open the page below or reload its tab to check your changes.",
      );
    } catch (e) {
      setError(
        typeof e.response?.data?.detail === "string"
          ? e.response.data.detail
          : "Could not save. Check your labels and links. Links must start with /, https://, http://, mailto:, or tel:.",
      );
    } finally {
      setBusy(false);
    }
  };
  const move = (i, delta) => {
    const list = [...form.navigation];
    [list[i], list[i + delta]] = [list[i + delta], list[i]];
    set("navigation", list);
  };
  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex flex-wrap justify-between gap-3 items-center">
          <div>
            <h1 className="text-2xl font-bold">Website</h1>
            <p className="text-sm text-gray-600 mt-2">
              Edit navigation, service pages, images, and shopping buttons. Save
              before checking the public page.
            </p>
          </div>
          <button
            className={button + " bg-[#F5A623] font-semibold"}
            disabled={!form || busy}
            onClick={save}
          >
            {busy ? "Saving…" : "Save website changes"}
          </button>
        </div>
        {error && (
          <p role="alert" className="text-red-700">
            {error}
          </p>
        )}
        {message && (
          <p role="status" className="text-green-800">
            {message}
          </p>
        )}
        <label className="block text-sm">
          Section
          <select
            className={field}
            value={section}
            onChange={(e) => setSection(e.target.value)}
          >
            <option value="navigation">Navigation and logo</option>
            {services.map((s) => (
              <option key={s.slug} value={s.slug}>
                {s.name}
              </option>
            ))}
            <option value="walkthrough">
              Product walkthrough and page content
            </option>
          </select>
        </label>
        {form && section === "navigation" && (
          <div className="space-y-6">
            <ImageField
              label="Site logo URL"
              value={form.logo}
              onChange={(v) => set("logo", v)}
              token={token}
            />
            <h2 className="text-lg font-semibold">Navigation links</h2>
            <p className="text-sm text-gray-600">
              Use the arrows to rearrange links. Services can keep its service
              dropdown. Up to 12 links; longer menus collapse into the menu
              button.
            </p>
            {form.navigation.map((n, i) => (
              <div key={i} className="border-b pb-4 space-y-2">
                <div className="grid sm:grid-cols-2 gap-3">
                  <label className="text-sm">
                    Link {i + 1} label
                    <input
                      className={field}
                      value={n.name}
                      onChange={(e) =>
                        set(
                          "navigation",
                          form.navigation.map((x, j) =>
                            j === i ? { ...x, name: e.target.value } : x,
                          ),
                        )
                      }
                    />
                  </label>
                  <label className="text-sm">
                    Destination
                    <input
                      className={field}
                      value={n.path}
                      onChange={(e) =>
                        set(
                          "navigation",
                          form.navigation.map((x, j) =>
                            j === i ? { ...x, path: e.target.value } : x,
                          ),
                        )
                      }
                    />
                  </label>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    className={button}
                    disabled={i === 0}
                    onClick={() => move(i, -1)}
                    aria-label={`Move ${n.name} up`}
                  >
                    ↑ Up
                  </button>
                  <button
                    className={button}
                    disabled={i === form.navigation.length - 1}
                    onClick={() => move(i, 1)}
                    aria-label={`Move ${n.name} down`}
                  >
                    ↓ Down
                  </button>
                  <button
                    className={button}
                    onClick={() =>
                      set(
                        "navigation",
                        form.navigation.filter((_, j) => j !== i),
                      )
                    }
                  >
                    Remove
                  </button>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={!!n.hasDropdown}
                      onChange={(e) =>
                        set(
                          "navigation",
                          form.navigation.map((x, j) =>
                            j === i
                              ? { ...x, hasDropdown: e.target.checked }
                              : x,
                          ),
                        )
                      }
                    />
                    Service dropdown
                  </label>
                </div>
              </div>
            ))}
            <button
              className={button}
              disabled={form.navigation.length >= 12}
              onClick={() =>
                set("navigation", [
                  ...form.navigation,
                  { name: "New link", path: "/shop" },
                ])
              }
            >
              Add navigation link
            </button>
            <div className="grid sm:grid-cols-2 gap-3">
              <label>
                Primary button label
                <input
                  className={field}
                  value={form.primary_label}
                  onChange={(e) => set("primary_label", e.target.value)}
                />
              </label>
              <label>
                Primary button destination
                <input
                  className={field}
                  value={form.primary_link}
                  onChange={(e) => set("primary_link", e.target.value)}
                />
              </label>
            </div>
            <Link className="underline" to="/" target="_blank">
              View homepage
            </Link>
          </div>
        )}
        {form &&
          services
            .filter((s) => s.slug === section)
            .map((base) => {
              const s = { ...base, ...form.services[base.slug] };
              return (
                <div key={s.slug} className="space-y-5">
                  <div className="flex flex-wrap gap-4 text-sm">
                    <Link
                      className="underline"
                      to={`/services/${s.slug}`}
                      target="_blank"
                    >
                      View service page
                    </Link>
                    <Link
                      className="underline"
                      to={s.button_link}
                      target="_blank"
                    >
                      Open shopping / quote link
                    </Link>
                    <button
                      className="underline"
                      onClick={() =>
                        navigator.clipboard
                          .writeText(
                            new URL(s.button_link, window.location.origin).href,
                          )
                          .then(() => setMessage("Link copied."))
                          .catch(() =>
                            setError(
                              "Could not copy. Open the link and copy its address.",
                            ),
                          )
                      }
                    >
                      Copy shopping link
                    </button>
                  </div>
                  {[
                    "name",
                    "tagline",
                    "shortDescription",
                    "description",
                    "callout",
                    "button_text",
                    "button_link",
                  ].map((k) => (
                    <label key={k} className="block text-sm">
                      {
                        {
                          name: "Page title",
                          tagline: "Tagline",
                          shortDescription: "Summary",
                          description: "Full description",
                          callout: "Callout",
                          button_text: "Button label",
                          button_link: "Button destination",
                        }[k]
                      }
                      {["description", "shortDescription"].includes(k) ? (
                        <textarea
                          rows={k === "description" ? 6 : 3}
                          className={field}
                          value={s[k] || ""}
                          onChange={(e) =>
                            updateService(s.slug, k, e.target.value)
                          }
                        />
                      ) : (
                        <input
                          className={field}
                          value={s[k] || ""}
                          onChange={(e) =>
                            updateService(s.slug, k, e.target.value)
                          }
                        />
                      )}
                    </label>
                  ))}
                  <ImageField
                    label="Service image URL"
                    value={s.image}
                    token={token}
                    onChange={(v) => updateService(s.slug, "image", v)}
                  />
                  <label className="block text-sm">
                    Features (one per line)
                    <textarea
                      rows={5}
                      className={field}
                      value={(s.features || []).join("\n")}
                      onChange={(e) =>
                        updateService(
                          s.slug,
                          "features",
                          e.target.value.split("\n"),
                        )
                      }
                    />
                  </label>
                  <h2 className="text-lg font-semibold">Process steps</h2>
                  {(s.process || []).map((p, i) => (
                    <div key={i} className="grid sm:grid-cols-3 gap-2">
                      {["step", "title", "desc"].map((k) => (
                        <label key={k} className="text-sm">
                          {k}
                          <input
                            className={field}
                            value={p[k] || ""}
                            onChange={(e) =>
                              updateService(
                                s.slug,
                                "process",
                                s.process.map((v, j) =>
                                  j === i ? { ...v, [k]: e.target.value } : v,
                                ),
                              )
                            }
                          />
                        </label>
                      ))}
                      <button
                        className={button}
                        onClick={() =>
                          updateService(
                            s.slug,
                            "process",
                            s.process.filter((_, j) => j !== i),
                          )
                        }
                      >
                        Remove step
                      </button>
                    </div>
                  ))}
                  <button
                    className={button}
                    onClick={() =>
                      updateService(s.slug, "process", [
                        ...(s.process || []),
                        {
                          step: String((s.process || []).length + 1),
                          title: "",
                          desc: "",
                        },
                      ])
                    }
                  >
                    Add step
                  </button>
                  <h2 className="text-lg font-semibold">Gallery</h2>
                  {(s.gallery || []).map((g, i) => (
                    <div key={i} className="border-b pb-4 space-y-2">
                      <ImageField
                        label={`Gallery image ${i + 1}`}
                        value={g.src}
                        token={token}
                        onChange={(v) =>
                          updateService(
                            s.slug,
                            "gallery",
                            s.gallery.map((x, j) =>
                              j === i ? { ...x, src: v } : x,
                            ),
                          )
                        }
                      />
                      {["alt", "caption"].map((k) => (
                        <label key={k} className="block text-sm">
                          {k === "alt" ? "Image description" : "Caption"}
                          <input
                            className={field}
                            value={g[k] || ""}
                            onChange={(e) =>
                              updateService(
                                s.slug,
                                "gallery",
                                s.gallery.map((x, j) =>
                                  j === i ? { ...x, [k]: e.target.value } : x,
                                ),
                              )
                            }
                          />
                        </label>
                      ))}
                      <button
                        className={button}
                        onClick={() =>
                          updateService(
                            s.slug,
                            "gallery",
                            s.gallery.filter((_, j) => j !== i),
                          )
                        }
                      >
                        Remove image
                      </button>
                    </div>
                  ))}
                  <button
                    className={button}
                    onClick={() =>
                      updateService(s.slug, "gallery", [
                        ...(s.gallery || []),
                        { src: "", alt: "", caption: "" },
                      ])
                    }
                  >
                    Add gallery image
                  </button>
                </div>
              );
            })}
        {section === "walkthrough" && (
          <div className="space-y-5 text-sm leading-relaxed">
            <h2 className="text-lg font-semibold">
              Publish and verify a product
            </h2>
            <ol className="list-decimal pl-5 space-y-3">
              <li>
                Open Products → Add Product. Choose Custom Apparel for
                configurable embroidery or printing, School Uniform for school
                items, or Ready-Made Merch for finished goods.
              </li>
              <li>
                Add its name, price, photos, sizes, colors, and category. School
                categories are optional for non-school merchandise.
              </li>
              <li>
                For custom apparel, select its garment type. Review Placements
                and any product overrides. Add setup or design fees only when
                applicable. School outerwear has its own editable name charge.
              </li>
              <li>
                Save while hidden, then use Preview saved product. Check every
                size, color, decoration, placement, and price. Preview disables
                purchases and uploads.
              </li>
              <li>
                Turn Active on and save. Use View Product to test the actual
                customer page, including artwork uploads and the cart. Copy
                product link gives the public address.
              </li>
              <li>
                Open the selected category shop. Confirm the product appears
                there and the Services Shop Now button leads to that collection.
              </li>
            </ol>
            <h2 className="text-lg font-semibold">
              Which editor controls which page?
            </h2>
            <p>
              Website controls the logo, navigation, service
              copy/photos/galleries, and service shopping buttons. Products
              controls product text/photos/options. Categories controls
              collection names and descriptions. Placements controls shared
              decoration rules.
            </p>
            <p>
              Smart Site controls the named homepage, Services introduction,
              About introduction, and page call-to-action sections. Choose the
              surface matching the page, edit its text/image/button fields, and
              save an active item. Keep general content without an intent
              category for a default shown to everyone. Intent-specific content
              may vary by visitor.
            </p>
            <Link to="/admin/settings/smart-site" className="underline">
              Open Smart Site
            </Link>
            <p>
              After saving, open the public page in a new tab and reload. Check
              both a phone and desktop layout. Do not send customers an
              /admin/preview link. A real checkout can charge a payment; use the
              cart to verify totals without placing an order.
            </p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
